import { Alert } from "@mui/material";
import { isNil } from "lodash";

import {Trans} from "../utils/i18n";
import DeviceWidget from "../FormComponents/DeviceWidget/DeviceWidget";
import { hasProducts } from "./orderEntryEnfore";
import { validateEmailRegEx, validatePhoneNumber } from "../Shared/FormField";
import PartMissingModal from "../Shared/PartMissingModal";

export const checkoutAmazonConfig = {
    sizes: {
        forth: 3,
        third: 4,
        half: 6,
        full: 12,
    },
    enableStepper: true,
    defaultValues: {
        productcategory: "all",
    },
    prepareSubmitData: (values) => {
        return {
            ...values,
            products: values.products
                // Only send the products that have an amount > 0
                .filter(product => !isNil(product.amount) && Number(product.amount) > 0)
                // Override the type to shop, so that the products can be handled correctly in the backend (e. g. price calculation)
                .map(product => ({
                    ...product,
                    type: "shop",
                })),
        };
    },
    heading: <Trans i18nKey="orderEntry.basicData">Basic data</Trans>,
    groups: [
        {
            description: (
                <Trans i18nKey={"orderEntry.description"}>
                    Your device is not behaving as you expect?{" "}
                    <a
                        href={"https://b2x.com/"}
                        title={"B2X"}
                        target={"_blank"}
                    >
                        B2X
                    </a>{" "}
                    support you from here to provide a solution quickly. Please
                    complete the form below and we will get back to you to
                    resolve the situation.
                </Trans>
            ),
            fields: [
                {
                    size: "full",
                    component: "requesttypeselect",
                    doNotResetForm: true,
                },
            ],
        },
        {
            heading: (
                <Trans i18nKey="orderEntry.addressContact">
                    Contact information
                </Trans>
            ),
            description: (
                <Trans i18nKey="orderEntry.addressContact.description">
                    Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed
                    diam nonumy eirmod tempor invidunt ut labore et dolore magna
                    aliquyam erat, sed diam voluptua.
                </Trans>
            ),
            id: "address",
            fields: [
                ({ values, form }) => ({
                    size: "full",
                    component: "shoppingcart",
                    products: values.products,
                    transportProductId: values.transport_product_id,
                    onChangeTransportProduct: (transportProduct) => form.change("transport_product_id", transportProduct.id),
                    onRemoveProduct: (product) => form.change("products", values.products?.filter(p => p?.id !== product.id) ?? []),
                    onUpdateProductQuantity: (productId, amount) => form.change("products", values.products?.map(p => p?.id === productId ? { ...p, amount } : p) ?? []),
                    className: "b2x-shadow-md bg-white p-8 mt-4",
                }),
                {
                    size: "full",
                    component: "callback",
                    callback: (values) => (
                        <>
                            <h2>
                                <Trans i18nKey={"orderEntry.contactInfo"}>
                                    Contact info
                                </Trans>
                            </h2>
                        </>
                    ),
                },
                {
                    name: "name",
                    label: (
                        <Trans i18nKey="orderEntry.addressContact.name">
                            Name
                        </Trans>
                    ),
                    placeholder: (
                        <Trans i18nKey="orderEntry.addressContact.name">
                            Name
                        </Trans>
                    ),
                    size: "half",
                    type: "text",
                    required: true,
                },
                {
                    name: "email",
                    label: (
                        <Trans i18nKey="orderEntry.addressContact.email">
                            E-Mail Address
                        </Trans>
                    ),
                    placeholder: (
                        <Trans i18nKey="orderEntry.addressContact.email">
                            E-Mail Address
                        </Trans>
                    ),
                    size: "half",
                    type: "email",
                    required: true,
                },
                {
                    name: "phone",
                    label: (
                        <Trans i18nKey="orderEntry.addressContact.phone">
                            Phone
                        </Trans>
                    ),
                    placeholder: (
                        <Trans i18nKey="orderEntry.addressContact.phoneNumber">
                            Phone number
                        </Trans>
                    ),
                    size: "full",
                    type: "phoneinput",
                    required: true,
                },
                {
                    size: "full",
                    component: "callback",
                    callback: (values) => (
                        <>
                            <h2>
                                <Trans i18nKey={"orderEntry.addressInfo"}>
                                    Address info
                                </Trans>
                            </h2>
                        </>
                    ),
                },
                {
                    size: "full",
                    component: "addressautocomplete",
                },
                {
                    name: "zip",
                    label: (
                        <Trans i18nKey="orderEntry.addressContact.zip">
                            ZIP
                        </Trans>
                    ),
                    size: "half",
                    required: true,
                    validatetype: "api",
                    validationRoute: route("order.validateZip"),
                },
                {
                    name: "city",
                    label: (
                        <Trans i18nKey="orderEntry.addressContact.city">
                            City
                        </Trans>
                    ),
                    size: "half",
                    required: true,
                },
                {
                    name: "country_id",
                    label: (
                        <Trans i18nKey="orderEntry.addressContact.country">
                            Country
                        </Trans>
                    ),
                    placeholder: (
                        <Trans i18nKey="orderEntry.addressContact.country.placeholder">
                            Please choose
                        </Trans>
                    ),
                    size: "full",
                    type: "select",
                    required: true,
                    onlyCurrentCountry: true,
                    disableUnselected: true,
                },
                {
                    name: "deviating_invoice_address",
                    label: (
                        <Trans i18nKey="orderEntry.deviating_invoice_address">
                            Different billing address
                        </Trans>
                    ),
                    size: "full",
                    type: "checkbox",
                },
                {
                    size: "full",
                    component: "callback",
                    callback: (values) => (
                        <>
                            <h2>
                                <Trans i18nKey={"orderEntry.invoice_address"}>
                                    Billing address
                                </Trans>
                            </h2>
                            <p>
                                <Trans
                                    i18nKey={
                                        "orderEntry.invoice_address.description"
                                    }
                                >
                                    Please fill in the address that will be
                                    displayed on the invoice.
                                </Trans>
                            </p>
                        </>
                    ),
                    conditionFunction: (values, orderEntryState) =>
                        values.deviating_invoice_address,
                },
                {
                    name: "invoice_name",
                    label: (
                        <Trans i18nKey="orderEntry.addressContact.name">
                            Name
                        </Trans>
                    ),
                    placeholder: (
                        <Trans i18nKey="orderEntry.addressContact.name">
                            Name
                        </Trans>
                    ),
                    size: "full",
                    type: "text",
                    required: true,
                    conditionFunction: (values, orderEntryState) =>
                        values.deviating_invoice_address,
                },
                {
                    size: "full",
                    component: "addressautocomplete",
                    prefix_name: "invoice_",
                    conditionFunction: (values, orderEntryState) =>
                        values.deviating_invoice_address,
                },
                {
                    name: "invoice_zip",
                    label: (
                        <Trans i18nKey="orderEntry.addressContact.zip">
                            ZIP
                        </Trans>
                    ),
                    size: "half",
                    required: true,
                    validatetype: "api",
                    validationRoute: route("order.validateZip"),
                    conditionFunction: (values, orderEntryState) =>
                        values.deviating_invoice_address,
                },
                {
                    name: "invoice_city",
                    label: (
                        <Trans i18nKey="orderEntry.addressContact.city">
                            City
                        </Trans>
                    ),
                    size: "half",
                    required: true,
                    conditionFunction: (values, orderEntryState) =>
                        values.deviating_invoice_address,
                },
                ({ form } ) => ({
                    name: "invoice_country_id",
                    label: (
                        <Trans i18nKey="orderEntry.addressContact.country">
                            Country
                        </Trans>
                    ),
                    placeholder: (
                        <Trans i18nKey="orderEntry.addressContact.country.placeholder">
                            Please choose
                        </Trans>
                    ),
                    size: "half",
                    type: "select",
                    required: true,
                    disableUnselected: true,
                    onChange: () => form.change("invoice_zip", ""),
                    conditionFunction: (values, orderEntryState) =>
                        values.deviating_invoice_address,
                }),
                {
                    size: "full",
                    component: "callback",
                    callback: (values, orderEntryState) => {
                        return (
                            <Alert severity="error">
                                <Trans i18nKey="orderEntry.zipBlacklist">
                                    Thank you for buying with us. Due to special VAT regimen in your region, please CONTACT US to proceed with your request
                                </Trans>
                            </Alert>
                        );
                    },
                    conditionFunction: (values, orderEntryState) => {
                        return orderEntryState?.zipBlacklist?.some?.(zip => zip.value === values.zip && zip.name === 'zip') || orderEntryState?.zipBlacklist?.some?.(zip => zip.value === values.invoice_zip && zip.name === 'invoice_zip');
                    },
                },
                {
                    size: "full",
                    component: "callback",
                    callback: (values, orderEntryState) => {
                        return (
                            <PartMissingModal productTitle="Produkt" />
                        );
                    },
                    conditionFunction: (values, orderEntryState) => {
                        return orderEntryState?.zipBlacklist?.some?.(zip => zip.value === values.zip && zip.name === 'zip') || orderEntryState?.zipBlacklist?.some?.(zip => zip.value === values.invoice_zip && zip.name === 'invoice_zip');
                    },
                },
            ],
            conditionFunction: (values, orderEntryState) => {
                return hasProducts(values.products);
            },
        },
        {
            heading: (
                <Trans i18nKey="common.checkout">Confirmation / Summary</Trans>
            ),
            description: (
                <Trans i18nKey="orderEntry.termsandconditions.description"></Trans>
            ),
            id: "terms",
            fields: [
                {
                    size: "full",
                    component: "callback",
                    callback: (values, orderEntryState) => {
                        const product = orderEntryState.selectedProduct;
                        return (
                            <>
                                <DeviceWidget
                                    values={values}
                                    hideBadge={true}
                                />
                            </>
                        );
                    },
                },
                {
                    size: "full",
                    component: "callback",
                    callback: (values) => (
                        <>
                            <h2 style={{marginBottom: 0}}>
                                <Trans
                                    i18nKey={"orderEntry.verifyAccountToPay"}
                                >
                                    Verify your information
                                </Trans>
                            </h2>
                        </>
                    ),
                },
                {
                    size: "full",
                    component: "verify_phone_number",
                },
                {
                    name: "terms_of_service",
                    label: (
                        <Trans i18nKey="orderEntry.termsandconditions.label">
                            I have read and accept the <a
                            href={route("shop.termsandconditions")}
                            target={"_blank"}
                        >
                            Terms & Conditions
                        </a> and the <a href={route("privacy")} target={"_blank"}>
                            Privacy Statement
                        </a>
                        </Trans>
                    ),
                    size: "full",
                    type: "checkbox",
                    required: true,
                },
            ],
            conditionFunction: (values, orderEntryState) => {
                const validAddress =
                    values.name &&
                    values.housenumber &&
                    values.city &&
                    values.street &&
                    values.zip &&
                    values.country_id &&
                    validatePhoneNumber.test(values.phone) &&
                    validateEmailRegEx.test(values.email) &&
                    !orderEntryState?.zipBlacklist?.some?.(zip => zip.value === values.zip && zip.name === 'zip')
                const validInvoiceAddress =
                    values.deviating_invoice_address == true
                        ? values.invoice_name &&
                        values.invoice_city &&
                        values.invoice_street &&
                        values.invoice_zip &&
                        values.invoice_country_id &&
                        !orderEntryState?.zipBlacklist?.some?.(zip => zip.value === values.invoice_zip && zip.name === 'invoice_zip')
                        : true;
                const validTransportProduct = !!values.transport_product_id;

                return (
                    validAddress &&
                    validInvoiceAddress &&
                    hasProducts(values.products) &&
                    validTransportProduct
                );
            },
        },
    ],
    actions: [
        {
            fields: [
                {
                    text: <Trans i18nKey="orderEntry.send">Send</Trans>,
                    size: "half",
                    component: "button",
                    action: "next",
                    color: "secondary",
                    conditionFunction: (values, orderEntryState) => {
                        const validAddress =
                            values.name &&
                            values.housenumber &&
                            values.city &&
                            values.street &&
                            values.zip &&
                            values.country_id &&
                            validatePhoneNumber.test(values.phone) &&
                            validateEmailRegEx.test(values.email);

                        return (
                            values.terms_of_service &&
                            orderEntryState?.verificationCodeResponse &&
                            !!validAddress &&
                            hasProducts(values.products)
                        );
                    },
                },
            ],
        },
    ],
};
